// dropdown2.js

import React, { useState, useRef, useEffect, useCallback } from "react";
import { ReactComponent as DropDownIcon } from "../icons/dropdown.svg";

const Dropdown = ({ label, options, selectedOptions, setSelectedOptions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => setIsOpen(!isOpen);

  // Helper function to extract internal values from options
  const getOptionValues = () => {
    return options.map((option) =>
      typeof option === "object" ? option.value : option
    );
  };

  // Function to handle option clicks
  const handleOptionClick = (optionValue) => {
    if (
      optionValue === "select_all" ||
      optionValue === "Select All" ||
      optionValue === "clear"
    ) {
      if (selectedOptions.length === getOptionValues().length) {
        // Clear all
        setSelectedOptions([]);
      } else {
        // Select all
        const allValues = getOptionValues();
        setSelectedOptions(allValues);
      }
    } else {
      let newSelectedOptions;
      if (selectedOptions.includes(optionValue)) {
        // Unchecking an option - also remove any "select all" values
        newSelectedOptions = selectedOptions.filter(
          (value) =>
            value !== optionValue &&
            value !== "select_all" &&
            value !== "Select All"
        );
      } else {
        // Checking an option
        newSelectedOptions = [...selectedOptions, optionValue];

        // If all individual options are now selected, add "select_all" back
        const allIndividualValues = getOptionValues().filter(
          (v) => v !== "select_all" && v !== "Select All"
        );
        const selectedIndividualValues = newSelectedOptions.filter(
          (v) => v !== "select_all" && v !== "Select All"
        );
        if (selectedIndividualValues.length === allIndividualValues.length) {
          // Add the appropriate "select all" value
          const selectAllValue = getOptionValues().find(
            (v) => v === "select_all" || v === "Select All"
          );
          if (selectAllValue && !newSelectedOptions.includes(selectAllValue)) {
            newSelectedOptions = [...newSelectedOptions, selectAllValue];
          }
        }
      }
      setSelectedOptions(newSelectedOptions);
    }
    // Close the dropdown after selecting an option
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  const handleClickOutside = useCallback(
    (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    },
    [dropdownRef]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  // Function to chunk options for layout purposes
  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  // Determine if options are objects or strings
  const isObjectOption = typeof options[0] === "object" && options[0] !== null;

  // Create chunks excluding the first option ("Select All")
  const optionChunks = chunkArray(options.slice(1), 5);

  // Check if all options are selected
  const allSelected = selectedOptions.length === getOptionValues().length;

  return (
    <div className={`dropdown ${isOpen ? "open" : ""}`} ref={dropdownRef}>
      <button className="dropdown-toggle" onClick={toggleDropdown}>
        {label} <DropDownIcon className="dropdown-icon" />
      </button>
      {isOpen && (
        <>
          <div
            className="dropdown-overlay"
            onClick={() => setIsOpen(false)}
          ></div>
          <div className="dropdown-menu2">
            <div
              className="dropdown-item dropdown-select-all"
              onClick={() =>
                handleOptionClick(allSelected ? "clear" : "select_all")
              }
            >
              {allSelected ? "Clear" : "Tout"}
            </div>
            {optionChunks.map((chunk, chunkIndex) => (
              <div key={chunkIndex} className="dropdown-row">
                {chunk.map((option) => {
                  const value = isObjectOption ? option.value : option;
                  const optionLabel = isObjectOption ? option.label : option;

                  return (
                    <div
                      key={value}
                      className="dropdown-item"
                      onClick={() => handleOptionClick(value)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedOptions.includes(value)}
                        readOnly
                      />
                      {optionLabel}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Dropdown;
